import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUser } from '../../../redux/usersRedux';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from '@mui/material';

import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import {
  finalizeProductionHandover,
  getProductionHandovers,
  requestProductionHandover,
  retryProductionHandover,
} from '../../../api/productionHandoverApi';

import { productionHandoverStatusLabels } from '../../../utils/productionHandoverLabels';
import ProductionHandoverDetailsDialog from './Components/ProductionHandoverDetailsDialog';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../../../config';

const socket = io(SOCKET_URL);

const emptyFilters = {
  status: '',
  productionOrder: '',
  batch: '',
  materialNumber: '',
  dateFrom: '',
  dateTo: '',
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const formatNumber = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }

  return new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 3,
  }).format(value);
};

const getStatusChipColor = (status) => {
  switch (status) {
    case 'AVAILABLE':
      return 'default';

    case 'REQUESTED':
      return 'info';

    case 'PROCESSING':
      return 'primary';

    case 'DATA_COLLECTED':
      return 'warning';

    case 'COMPLETED':
      return 'success';

    case 'DISCREPANCY':
      return 'error';

    case 'FAILED':
      return 'error';

    default:
      return 'default';
  }
};

const productionHandoverActionRoles = [
  'ADMIN',
  'KONTROLER',
  'STARSZY_MAGAZYNIER',
];

const ProductionHandoverPage = () => {
  const user = useSelector((state) => getUser(state));
  const [filters, setFilters] = useState(emptyFilters);

  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [items, setItems] = useState([]);

  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);

  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [error, setError] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);

  const [selectedHandoverId, setSelectedHandoverId] = useState(null);

  const [pendingAction, setPendingAction] = useState(null);

  const [notification, setNotification] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const openActionConfirmation = (type, item) => {
    setPendingAction({
      type,
      item,
    });
  };

  const closeActionConfirmation = () => {
    if (actionLoadingId) {
      return;
    }

    setPendingAction(null);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getProductionHandovers({
        ...appliedFilters,
        page: page + 1,
        limit: rowsPerPage,
      });

      setItems(Array.isArray(result.items) ? result.items : []);

      setTotal(result.pagination?.total || 0);
    } catch (loadError) {
      console.error(loadError);

      setError('Nie udało się pobrać listy przekazań produkcji.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, rowsPerPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleChanged = (eventData) => {
      console.log('Zmiana przekazania produkcji:', eventData);

      const isAdmin = user?.users?.role === 'ADMIN';

      const isDifferentWarehouse =
        eventData?.warehouse &&
        user?.users?.warehouse &&
        eventData.warehouse !== user.users.warehouse;

      if (!isAdmin && isDifferentWarehouse) {
        return;
      }

      loadData();
    };

    socket.on('productionHandoverChanged', handleChanged);

    return () => {
      socket.off('productionHandoverChanged', handleChanged);
    };
  }, [loadData, user?.users?.role, user?.users?.warehouse]);

  const handleFilterChange = (field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    setPage(0);
    setAppliedFilters({
      ...filters,
    });
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(0);
  };

  //   const handleRequest = async (id) => {
  //     setActionLoadingId(id);
  //     setError(null);

  //     try {
  //       await requestProductionHandover(id);
  //       await loadData();
  //     } catch (requestError) {
  //       console.error(requestError);

  //       setError('Nie udało się zlecić analizy.');
  //     } finally {
  //       setActionLoadingId(null);
  //     }
  //   };

  //   const handleRetry = async (id) => {
  //     setActionLoadingId(id);
  //     setError(null);

  //     try {
  //       await retryProductionHandover(id);
  //       await loadData();
  //     } catch (retryError) {
  //       console.error(retryError);

  //       setError('Nie udało się ponowić analizy.');
  //     } finally {
  //       setActionLoadingId(null);
  //     }
  //   };

  //   const handleFinalize = async (id) => {
  //     setActionLoadingId(id);
  //     setError(null);

  //     try {
  //       await finalizeProductionHandover(id);
  //       await loadData();
  //     } catch (finalizeError) {
  //       console.error(finalizeError);

  //       setError('Nie udało się ponowić finalizacji.');
  //     } finally {
  //       setActionLoadingId(null);
  //     }
  //   };

  const executePendingAction = async () => {
    if (!pendingAction?.item?.id) {
      return;
    }

    const { type, item } = pendingAction;

    setActionLoadingId(item.id);
    setError(null);

    try {
      if (type === 'REQUEST') {
        await requestProductionHandover(item.id);

        setNotification({
          open: true,
          severity: 'success',
          message: `Zlecono analizę zlecenia ${item.productionOrder}.`,
        });
      }

      if (type === 'RETRY') {
        await retryProductionHandover(item.id);

        setNotification({
          open: true,
          severity: 'success',
          message: `Ponowiono analizę zlecenia ${item.productionOrder}.`,
        });
      }

      if (type === 'FINALIZE') {
        await finalizeProductionHandover(item.id);

        setNotification({
          open: true,
          severity: 'success',
          message: `Zlecenie ${item.productionOrder} zostało sfinalizowane.`,
        });
      }

      setPendingAction(null);
      await loadData();
    } catch (actionError) {
      console.error(actionError);

      const responseMessage = actionError?.response?.data?.message;

      setNotification({
        open: true,
        severity: 'error',
        message: responseMessage || 'Nie udało się wykonać operacji.',
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleOpenDetails = (item) => {
    setSelectedHandoverId(item.id);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedHandoverId(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));

    setPage(0);
  };

  const canManageProductionHandover = productionHandoverActionRoles.includes(
    user?.users?.role,
  );

  const getActionDialogTitle = () => {
    switch (pendingAction?.type) {
      case 'REQUEST':
        return 'Zlecić analizę?';

      case 'RETRY':
        return 'Ponowić analizę?';

      case 'FINALIZE':
        return 'Ponowić finalizację?';

      default:
        return 'Potwierdź operację';
    }
  };

  const getActionDialogText = () => {
    const productionOrder = pendingAction?.item?.productionOrder;

    switch (pendingAction?.type) {
      case 'REQUEST':
        return (
          `Zlecenie ${productionOrder} zostanie ` +
          'przekazane do analizy przez bota.'
        );

      case 'RETRY':
        if (pendingAction?.item?.status === 'DISCREPANCY') {
          return (
            `Poprzednia analiza zlecenia ${productionOrder} ` +
            'wykazała niezgodność. Poprzedni wynik zostanie ' +
            'wyczyszczony, a bot wykona pełną analizę ponownie.'
          );
        }

        return (
          `Zlecenie ${productionOrder} zostanie ` +
          'ponownie przekazane do analizy przez bota.'
        );

      case 'FINALIZE':
        return (
          `System ponowi finalizację zlecenia ` +
          `${productionOrder} i wysłanie wiadomości e-mail.`
        );

      default:
        return '';
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        justifyContent="space-between"
        alignItems={{
          xs: 'flex-start',
          md: 'center',
        }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Przekazanie produkcji</Typography>

        <Tooltip title="Odśwież">
          <span>
            <IconButton onClick={loadData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 2,
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
        >
          <TextField
            label="Zlecenie produkcyjne"
            value={filters.productionOrder}
            onChange={(event) =>
              handleFilterChange('productionOrder', event.target.value)
            }
            size="small"
          />

          <TextField
            label="Partia"
            value={filters.batch}
            onChange={(event) =>
              handleFilterChange('batch', event.target.value)
            }
            size="small"
          />

          <TextField
            label="Numer materiału"
            type="number"
            value={filters.materialNumber}
            onChange={(event) =>
              handleFilterChange('materialNumber', event.target.value)
            }
            size="small"
          />

          <FormControl
            size="small"
            sx={{
              minWidth: 220,
            }}
          >
            <InputLabel>Status</InputLabel>

            <Select
              label="Status"
              value={filters.status}
              onChange={(event) =>
                handleFilterChange('status', event.target.value)
              }
            >
              <MenuItem value="">Wszystkie</MenuItem>

              {Object.entries(productionHandoverStatusLabels).map(
                ([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>

          <TextField
            label="Data od"
            type="date"
            value={filters.dateFrom}
            onChange={(event) =>
              handleFilterChange('dateFrom', event.target.value)
            }
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
            sx={{
              width: 190,
            }}
          />

          <TextField
            label="Data do"
            type="date"
            value={filters.dateTo}
            onChange={(event) =>
              handleFilterChange('dateTo', event.target.value)
            }
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
            sx={{
              width: 190,
            }}
          />

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
          >
            Szukaj
          </Button>

          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
          >
            Wyczyść
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={2}>
        {loading ? (
          <Box
            sx={{
              minHeight: 250,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Zlecenie</TableCell>

                    <TableCell>Materiał</TableCell>

                    <TableCell>Partia</TableCell>

                    <TableCell>Linia</TableCell>

                    <TableCell>Status</TableCell>

                    <TableCell align="right">HU produkcja</TableCell>

                    <TableCell align="right">HU EWM</TableCell>

                    <TableCell align="right">Niezgodności</TableCell>

                    <TableCell>Data wyniku</TableCell>

                    <TableCell align="right">Akcje</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {items.map((item) => {
                    const actionLoading = actionLoadingId === item.id;

                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.productionOrder}</TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {item.materialNumberSnapshot ?? '-'}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {item.productNameSnapshot || ''}
                          </Typography>
                        </TableCell>

                        <TableCell>{item.batch || '-'}</TableCell>

                        <TableCell>{item.productionLine || '-'}</TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              productionHandoverStatusLabels[item.status] ||
                              item.status
                            }
                            color={getStatusChipColor(item.status)}
                          />
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(item.productionHuCount)}
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(item.ewmConfirmedHuCount)}
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(item.discrepancyHuCount)}
                        </TableCell>

                        <TableCell>
                          {formatDate(item.resultGeneratedAt)}
                        </TableCell>

                        <TableCell align="right">
                          <Stack
                            direction="row"
                            justifyContent="flex-end"
                            spacing={0.5}
                          >
                            <Tooltip title="Szczegóły">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDetails(item)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>

                            {canManageProductionHandover &&
                              item.status === 'AVAILABLE' && (
                                <Tooltip title="Zleć analizę">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={actionLoading}
                                      onClick={() =>
                                        openActionConfirmation('REQUEST', item)
                                      }
                                    >
                                      <PlayArrowIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}

                            {canManageProductionHandover &&
                              ['FAILED', 'DISCREPANCY'].includes(
                                item.status,
                              ) && (
                                <Tooltip
                                  title={
                                    item.status === 'DISCREPANCY'
                                      ? 'Ponów analizę po wyjaśnieniu niezgodności'
                                      : 'Ponów analizę'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={actionLoading}
                                      onClick={() =>
                                        openActionConfirmation('RETRY', item)
                                      }
                                    >
                                      <ReplayIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}

                            {canManageProductionHandover &&
                              item.status === 'DATA_COLLECTED' && (
                                <Tooltip title="Ponów finalizację">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={actionLoading}
                                      onClick={() =>
                                        openActionConfirmation('FINALIZE', item)
                                      }
                                    >
                                      <SendIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                        Brak przekazań spełniających kryteria.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Wierszy na stronie:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} z ${count !== -1 ? count : `więcej niż ${to}`}`
              }
            />
          </>
        )}
      </Paper>
      <ProductionHandoverDetailsDialog
        open={detailsOpen}
        handoverId={selectedHandoverId}
        onClose={handleCloseDetails}
      />
      <Dialog
        open={Boolean(pendingAction)}
        onClose={closeActionConfirmation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{getActionDialogTitle()}</DialogTitle>

        <DialogContent>
          <DialogContentText>{getActionDialogText()}</DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeActionConfirmation}
            disabled={Boolean(actionLoadingId)}
          >
            Anuluj
          </Button>

          <Button
            variant="contained"
            onClick={executePendingAction}
            disabled={Boolean(actionLoadingId)}
            startIcon={
              actionLoadingId ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            Potwierdź
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() =>
          setNotification((previous) => ({
            ...previous,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          severity={notification.severity}
          variant="filled"
          onClose={() =>
            setNotification((previous) => ({
              ...previous,
              open: false,
            }))
          }
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductionHandoverPage;
